import { NodeDimensionChange, useReactFlow, useStoreApi } from '@xyflow/react';
import { useCallback, useMemo } from 'react';

import {
  GraphVizIconShape,
  GraphVizNodeComponentType,
  parseIconShape,
} from '@/modules/graphViz/graphVizNodeConfig';

import { Node } from '../types';
import { isTileNode } from '../utils/nodes';

/**
 * Shared hook for reading and changing the icon shape of selected icon tiles.
 * Used by the EntityMap (ownership) and EntityDiagram (single entity) edit
 * trowsers. Uses `updateNode` for immediate-mode updates that flow through
 * each diagram's `onNodesChange` → reducer path.
 *
 * The estate waterfall has its own version at
 * `src/modules/estateWaterfall/hooks/useSelectedIconShape.ts` that dispatches
 * `UPDATE_TILE_CONFIG` through its reducer for undo/redo support.
 */
export function useSelectedIconShape(nodes: Node[]) {
  const { updateNode, getNode } = useReactFlow();
  const storeApi = useStoreApi();

  const selectedIconTiles = useMemo(
    () =>
      nodes.filter(isTileNode).filter(
        (n) =>
          !!n.selected &&
          // Tiles can be icons via componentType (entity map / waterfall) or
          // via the isIconVariant flag (entity diagram graph generation).
          (n.data.componentType === GraphVizNodeComponentType.TileIcon ||
            n.data.isIconVariant === true)
      ),
    [nodes]
  );

  const hasIconTilesSelected = selectedIconTiles.length > 0;

  const { currentShape, isMixed } = useMemo((): {
    currentShape: GraphVizIconShape;
    isMixed: boolean;
  } => {
    if (selectedIconTiles.length === 0)
      return { currentShape: GraphVizIconShape.Circle, isMixed: false };

    const firstShape =
      parseIconShape(selectedIconTiles[0]?.data.iconShape) ??
      GraphVizIconShape.Circle;
    const allSame = selectedIconTiles.every(
      (n) =>
        (parseIconShape(n.data.iconShape) ?? GraphVizIconShape.Circle) ===
        firstShape
    );

    return allSame
      ? { currentShape: firstShape, isMixed: false }
      : { currentShape: GraphVizIconShape.Circle, isMixed: true };
  }, [selectedIconTiles]);

  const setIconShape = useCallback(
    (shape: GraphVizIconShape) => {
      for (const tile of selectedIconTiles) {
        const n = getNode(tile.id);
        if (!n) continue;

        // Merge iconShape into data AND clear height in a single updateNode
        // call so the change flows through onNodesChange → reducer.
        // A separate updateNodeData call would only update React Flow's
        // internal store without triggering onNodesChange, causing the
        // controlled nodes prop to overwrite the change on re-render.
        updateNode(tile.id, {
          width: n.width,
          height: undefined,
          measured: {
            width: n.measured?.width,
            height: undefined,
          },
          data: {
            ...n.data,
            iconShape: shape,
            dimensionsFinalized: undefined,
          },
        });

        // Auto-width tiles (entity diagram) drive their own width via
        // data.width's min/max object and ReactFlow's offsetWidth-based
        // measurement is integer-rounded — pinning node.width with
        // setAttributes:true forces the wrapper to a width 1px below the
        // sub-pixel natural fit, causing text like "Waystar Royco" to wrap.
        // Their save paths don't persist node.height either, so skip the
        // re-measure dance entirely.
        const dataWidth = tile.data.width;
        if (typeof dataWidth === 'object' && dataWidth.width === 'auto') {
          continue;
        }

        // After ReactFlow re-measures the tile at its new natural height,
        // dispatch a dimensions change with setAttributes: true so node.height
        // (not just node.measured.height) is written to the new value. Without
        // this, node.height stays undefined and save paths that persist height
        // (e.g. entity map) fall back to the builder default on reload, causing
        // clipped content. Two rAFs: first lets React render with undefined
        // height, second lets ReactFlow's ResizeObserver measure.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const updated = getNode(tile.id);
            if (!updated) return;

            const measuredHeight = updated.measured?.height ?? updated.height;
            const measuredWidth = updated.measured?.width ?? updated.width;
            if (measuredHeight === undefined || measuredWidth === undefined) {
              return;
            }

            const dimensionChange: NodeDimensionChange = {
              id: tile.id,
              type: 'dimensions',
              dimensions: { width: measuredWidth, height: measuredHeight },
              setAttributes: true,
            };

            storeApi.getState().triggerNodeChanges([dimensionChange]);
          });
        });
      }
    },
    [selectedIconTiles, updateNode, getNode, storeApi]
  );

  return { hasIconTilesSelected, currentShape, isMixed, setIconShape };
}
